import React, { createContext, useContext, useState, useEffect, useMemo, useCallback } from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

axios.defaults.withCredentials = true;

// Silent session renewal: when any request 401s because the short-lived access
// token expired, transparently hit /auth/refresh ONCE (using the long-lived
// refresh cookie) and replay the original request. Concurrent 401s share one
// refresh call. This keeps users logged in for the full refresh window (30d)
// instead of being kicked out hourly.
let _refreshPromise = null;
axios.interceptors.response.use(
  (r) => r,
  async (error) => {
    const { response, config } = error;
    if (!response || response.status !== 401 || !config || config._retry) {
      return Promise.reject(error);
    }
    const url = config.url || '';
    if (url.includes('/auth/refresh') || url.includes('/auth/login') || url.includes('/auth/register')) {
      return Promise.reject(error);
    }
    config._retry = true;
    try {
      if (!_refreshPromise) {
        _refreshPromise = axios.post(`${API}/auth/refresh`).finally(() => { _refreshPromise = null; });
      }
      await _refreshPromise;
      return axios(config);
    } catch (e) {
      return Promise.reject(error);
    }
  }
);

const AuthContext = createContext();

const getRef = () => {
  try { return localStorage.getItem('kolv_ref') || undefined; } catch (e) { return undefined; }
};

function formatApiErrorDetail(detail) {
  if (detail == null) return 'Something went wrong. Please try again.';
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === 'string' ? e.msg : JSON.stringify(e))).filter(Boolean).join(' ');
  if (detail && typeof detail.msg === 'string') return detail.msg;
  return String(detail);
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/auth/me`);
      setUser(data);
    } catch (e) {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // CRITICAL: If returning from Google OAuth callback, skip the /me check.
    // The Welcome page exchanges the session_id and establishes the session first.
    if (typeof window !== 'undefined' && window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }
    checkAuth();
  }, [checkAuth]);

  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const loginWithGoogle = useCallback(async (sessionId) => {
    try {
      const ref = getRef();
      const { data } = await axios.post(`${API}/auth/google/session`, ref ? { session_id: sessionId, ref } : { session_id: sessionId });
      setUser(data);
      return { success: true, user: data };
    } catch (e) {
      return { success: false, error: formatApiErrorDetail(e.response?.data?.detail) || e.message };
    }
  }, []);

  // Self-managed Google OAuth: exchange the popup authorization code for our JWT
  // cookies. Works on custom domains where Emergent-managed Google Auth cannot.
  const loginWithGoogleCode = useCallback(async (code) => {
    try {
      const ref = getRef();
      const { data } = await axios.post(`${API}/auth/google/code`, ref ? { code, ref } : { code });
      setUser(data);
      return { success: true, user: data };
    } catch (e) {
      return { success: false, error: formatApiErrorDetail(e.response?.data?.detail) || e.message };
    }
  }, []);

  const loginWithFacebook = useCallback(async (accessToken) => {
    try {
      const ref = getRef();
      const { data } = await axios.post(`${API}/auth/facebook`, ref ? { access_token: accessToken, ref } : { access_token: accessToken });
      setUser(data);
      return { success: true, user: data };
    } catch (e) {
      return { success: false, error: formatApiErrorDetail(e.response?.data?.detail) || e.message };
    }
  }, []);

  const setLanguagePref = useCallback(async (language) => {
    try {
      await axios.post(`${API}/auth/language`, { language });
      setUser((u) => (u ? { ...u, language } : u));
      return { success: true };
    } catch (e) {
      return { success: false };
    }
  }, []);

  const login = useCallback(async (email, password) => {
    try {
      const { data } = await axios.post(`${API}/auth/login`, { email, password });
      setUser(data);
      return { success: true, user: data };
    } catch (e) {
      const detail = e.response?.data?.detail;
      // Backend returns a dict with {code, message} when email is not verified
      if (detail && typeof detail === 'object' && detail.code === 'email_not_verified') {
        return { success: false, error: detail.message, emailNotVerified: true, email };
      }
      return { success: false, error: formatApiErrorDetail(detail) || e.message };
    }
  }, []);

  const register = useCallback(async (userData) => {
    try {
      // Prefer a code typed into the form, fall back to the one captured from a link.
      const { ref: explicitRef, ...rest } = userData;
      const ref = (explicitRef && explicitRef.trim()) || getRef();
      const { data } = await axios.post(`${API}/auth/register`, ref ? { ...rest, ref } : rest);
      // New flow: backend returns {requires_verification: true} and does NOT issue cookies.
      if (data && data.requires_verification) {
        return { success: true, requiresVerification: true, email: data.email };
      }
      // Legacy / direct-login flow (e.g., if verification is disabled):
      setUser(data);
      return { success: true };
    } catch (e) {
      return { success: false, error: formatApiErrorDetail(e.response?.data?.detail) || e.message };
    }
  }, []);

  const logout = useCallback(async () => {
    try {
      await axios.post(`${API}/auth/logout`);
    } catch (e) {
      console.error('Logout error:', e);
    } finally {
      setUser(null);
    }
  }, []);

  const updateProfile = useCallback(async (updateData) => {
    try {
      const { data } = await axios.put(`${API}/auth/profile`, updateData);
      setUser(data);
      return { success: true };
    } catch (e) {
      return { success: false, error: formatApiErrorDetail(e.response?.data?.detail) || e.message };
    }
  }, []);

  const value = useMemo(() => ({
    user, loading, login, register, logout, updateProfile, checkAuth, loginWithGoogle, loginWithGoogleCode, loginWithFacebook, setLanguagePref
  }), [user, loading, login, register, logout, updateProfile, checkAuth, loginWithGoogle, loginWithGoogleCode, loginWithFacebook, setLanguagePref]);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
